import { lazy, Suspense } from 'react';
import { useLocation } from 'react-router';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';

// Lazy-loaded components
const PurchaseBundle = lazy(async () => ({
  default: (await import('pages/PurchaseBundle/PurchaseBundle')).PurchaseBundle,
}));
const CreateAccountComplete = lazy(async () => ({
  default: (await import('pages/CreateAccountComplete/CreateAccountComplete'))
    .CreateAccountComplete,
}));
const CreateAccount = lazy(async () => ({
  default: (await import('pages/Onboarding2025/CreateAccount/CreateAccount'))
    .CreateAccount,
}));
const OnboardingRoutes = lazy(async () => ({
  default: (await import('pages/Onboarding/OnboardingRoutes')).OnboardingRoutes,
}));
const Onboarding2025Routes = lazy(async () => ({
  default: (await import('pages/Onboarding2025/OnboardingRoutes'))
    .OnboardingRoutes,
}));
const KeywordExplorerOrUpsell = lazy(async () => ({
  default: (await import('pages/KeywordExplorer/KeywordExplorer'))
    .KeywordExplorerOrUpsell,
}));
const BillingHome = lazy(async () => ({
  default: (await import('pages/BillingHome/BillingHome')).BillingHome,
}));
const SignUp = lazy(async () => ({
  default: (await import('pages/SignUp/SignUp')).SignUp,
}));
const SmoothUpgrade = lazy(async () => ({
  default: (await import('pages/SmoothUpgrade/SmoothUpgrade')).SmoothUpgrade,
}));
const SubscriptionUpdated = lazy(async () => ({
  default: (await import('pages/SubscriptionUpdated/SubscriptionUpdated'))
    .SubscriptionUpdated,
}));
const Analytics = lazy(async () => ({
  default: (await import('pages/Analytics/Analytics')).Analytics,
}));
const DomainOverview = lazy(async () => ({
  default: (await import('pages/DomainOverview/DomainOverview')).DomainOverview,
}));
const Go = lazy(async () => ({
  default: (await import('pages/Go/Go')).Go,
}));
const Agency = lazy(async () => ({
  default: (await import('pages/Agency/Agency')).Agency,
}));
const CreateClient = lazy(async () => ({
  default: (await import('pages/Agency/CreateClient/CreateClient'))
    .CreateClient,
}));
const IndexRedirect = lazy(async () => ({
  default: (await import('pages/IndexRedirect/IndexRedirect')).IndexRedirect,
}));
const BacklinkAnalysis = lazy(async () => ({
  default: (await import('pages/BacklinkAnalysis/BacklinkAnalysis'))
    .BacklinkAnalysis,
}));
const ContentOverview = lazy(async () => ({
  default: (await import('pages/ContentOverview/ContentOverview'))
    .ContentOverview,
}));
const SearchPerformance = lazy(async () => ({
  default: (await import('pages/SearchPerformance/SearchPerformance'))
    .SearchPerformance,
}));
const Site = lazy(async () => ({
  default: (await import('pages/Site/Site')).Site,
}));
const SiteAuditRoutes = lazy(async () => ({
  default: (await import('pages/SiteAudit/SiteAudit.routes')).SiteAuditRoutes,
}));
const SiteSettingsDelete = lazy(async () => ({
  default: (await import('pages/SiteSettings/Delete/Delete')).Delete,
}));
const SuperActionItemTemplatesUpsert = lazy(async () => ({
  default: (await import('pages/Super/ActionItemTemplates/Upsert/Upsert'))
    .Upsert,
}));
const SuperActionItemTemplatesList = lazy(async () => ({
  default: (await import('pages/Super/ActionItemTemplates/List/List')).List,
}));
const SuperAuthorizedPage = lazy(async () => ({
  default: (await import('pages/Super/SuperAuthorizedPage'))
    .SuperAuthorizedPage,
}));
const SuperBundlesList = lazy(async () => ({
  default: (await import('pages/Super/Bundles/List/List')).List,
}));
const SuperBundlesCreate = lazy(async () => ({
  default: (await import('pages/Super/Bundles/Create/Create')).Create,
}));
const SuperFeaturesCreate = lazy(async () => ({
  default: (await import('pages/Super/Features/Create/Create')).Create,
}));
const SuperFeaturesList = lazy(async () => ({
  default: (await import('pages/Super/Features/List/List')).List,
}));
const SuperFeaturesEdit = lazy(async () => ({
  default: (await import('pages/Super/Features/Edit/Edit')).Edit,
}));
const SuperProductsEdit = lazy(async () => ({
  default: (await import('pages/Super/Products/Edit/Edit')).Edit,
}));
const SuperProductsList = lazy(async () => ({
  default: (await import('pages/Super/Products/List/List')).List,
}));
const TeamMembersRoutes = lazy(async () => ({
  default: (await import('pages/TeamMembers/TeamMembers.routes'))
    .TeamMembersRoutes,
}));
const AcceptInvite = lazy(async () => ({
  default: (await import('pages/TeamMembers/AcceptInvite/AcceptInvite'))
    .AcceptInvite,
}));
const GoogleSearchConsole = lazy(async () => ({
  default: (
    await import('pages/SiteSettings/GoogleSearchConsole/GoogleSearchConsole')
  ).GoogleSearchConsole,
}));
const SwitchAccount = lazy(async () => ({
  default: (await import('pages/SwitchAccount/SwitchAccount')).SwitchAccount,
}));
const CallTrackingRoutes = lazy(async () => ({
  default: (await import('pages/CallTracking/CallTracking.routes'))
    .CallTrackingRoutes,
}));

function SuspenseWithFallback({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  return (
    <Suspense
      fallback={<CenteredPageLoadingCarrot title={title} />}
      key={location.key}
    >
      {children}
    </Suspense>
  );
}

export {
  AcceptInvite,
  Agency,
  Analytics,
  BacklinkAnalysis,
  BillingHome,
  CallTrackingRoutes,
  ContentOverview,
  CreateAccount,
  CreateAccountComplete,
  CreateClient,
  DomainOverview,
  Go,
  GoogleSearchConsole,
  IndexRedirect,
  KeywordExplorerOrUpsell,
  Onboarding2025Routes,
  OnboardingRoutes,
  PurchaseBundle,
  SearchPerformance,
  SignUp,
  Site,
  SiteAuditRoutes,
  SiteSettingsDelete,
  SmoothUpgrade,
  SubscriptionUpdated,
  SuperActionItemTemplatesList,
  SuperActionItemTemplatesUpsert,
  SuperAuthorizedPage,
  SuperBundlesCreate,
  SuperBundlesList,
  SuperFeaturesCreate,
  SuperFeaturesEdit,
  SuperFeaturesList,
  SuperProductsEdit,
  SuperProductsList,
  SuspenseWithFallback,
  SwitchAccount,
  TeamMembersRoutes,
};
